import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import uniqueId from 'lodash.uniqueid';
import {SelectionHeader, SelectionRow} from 'src/components/data-table-selection';
import {clearSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {ErrorFlatButton} from 'src/components/error-flat-button';
import {AccountLink, UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {ColumnValueTypes} from 'src/components/with-interaction-table/constants';
import {InteractiveCell} from 'src/components/with-interaction-table/interactive-cell';
import {WithInteractionTable} from 'src/components/with-interaction-table/with-interaction-table';
import {DEFAULT_PAGE_SIZES} from 'src/hooks/common-data-service/cached-table';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {PLACE, STICKY_HEADER_OFFSET} from 'src/modules/create-user/constants';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowData,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Tooltip} from '@pb/kit-component-popups';
import {TDataType} from '@pb/kit-component-table-interactions';
import {LocaleContext} from '@pb/kit-context-locale';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';


import {
    applyUpdatedData,
    selectBulkUserImportTemplateData,
    selectBulkUserImportValidateStepPagination,
    setCurrentPage,
    setPageSize,
    updateColumnByIds,
} from '../../services/user-bulk-import.slice';
import {getValidationHeader, validateColumnValue} from '../../utils';

import {COLUMNS, COLUMNS_DATA, EXISTED_USER_MESSAGE, HEADERS, SELECTION_KEY} from './constants';
import {
    RemoveRecord,
    SetCountry, SetGroup, SetNewsletters, SetStatus, SetType,
} from './tools';
import {useValidateColumnsApi} from './use-validate-columns-api';
import {useValidateColumnsTracking} from './use-validate-columns-tracking';

import {validateFileStepCell} from './validate-file-step.module.styl';

import type {
    InteractiveColumn,
    UpdatedData,
} from 'src/components/with-interaction-table/typedef';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type AccountValue = {
    accountId: number,
    accountName: string,
};

type GroupValue = {
    id: number | string,
    name?: string,
};

type TemplateColumnValue = {
    value?: string | number | AccountValue | GroupValue | null,
    validationError?: string,
};

export const DATA_TABLE_ID = 'validate-file-step-data-table';

type Props = {
    accountId: number,
    availableStatusCodes: string[],
};

export const ValidateFileStep = ({accountId, availableStatusCodes}: Props) => {
    const dispatch = useDispatch();
    const templateData = useSelector(selectBulkUserImportTemplateData);
    const {currentPage, pageSize} = useSelector(selectBulkUserImportValidateStepPagination);
    const {locale} = useContext(LocaleContext);

    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    const {newsletterLabels} = useNewsletterDictionary();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows: RowData[] = useMemo(() => (Array.isArray(templateData) ? (
        templateData
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .filter(user => user.id)
            .map(user => ({
                id: user.id,
                columnValues: {
                    email: {
                        ...user.email,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    firstName: {
                        value: user.firstName.value,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    lastName: {
                        value: user.lastName.value,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    userType: {
                        value: getUserTypeByCode(user.userType)?.description || '',
                        columnValueType: ColumnValueTypes.USER_DEFINED_SELECT,
                    },
                    userStatus: {
                        value: getUserStatusByCode(user.userStatus)?.description || '',
                        columnValueType: ColumnValueTypes.USER_DEFINED_SELECT,
                    },
                    targetAccount: {
                        value: user.targetAccount,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    priorAccount: {
                        value: user.priorAccount,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    country: {
                        value: user.country?.description || '',
                        columnValueType: ColumnValueTypes.USER_DEFINED_SELECT,
                    },
                    group: {
                        value: user.group,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    jobTitle: {
                        value: user.jobTitle.value,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    city: {
                        value: user.city.value,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                    newsletters: {
                        value: user.newsletters?.map(newsletter => newsletterLabels[newsletter])
                            .join('; ') || 'No Newsletter',
                        columnValueType: ColumnValueTypes.USER_DEFINED_SELECT,
                    },
                    notes: {
                        value: user.notes?.value,
                        columnValueType: ColumnValueTypes.STRING,
                    },
                },
            }))
    ) : []), [templateData, getUserTypeByCode, getUserStatusByCode, newsletterLabels, currentPage, pageSize]);

    const columns = useMemo(() => COLUMNS_DATA.map((column: InteractiveColumn) => ({
        ...column,
        width: getColumnWidthByColumnId(column.columnId),
    })), [getColumnWidthByColumnId]);

    const getCurrentColumn = useCallbackWithRef(columnIndex => {
        const column = columns[columnIndex];
        return column.columnType ? column : null;
    });
    const dataApi = useValidateColumnsApi({getCurrentColumn});
    const trackingApi = useValidateColumnsTracking();

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onChangePageSize = useCallback((_currentPage: number, newPageSize: number) => {
        dispatch(setPageSize(newPageSize));
    }, [dispatch]);

    const onPaginationChange = useCallback((changedPagination: Pagination) => {
        dispatch(applyUpdatedData());
        dispatch(setCurrentPage(changedPagination.currentPage));
    }, [dispatch]);

    const onValidate = useCallback((invalidData: UpdatedData[]) => {
        dispatch(updateColumnByIds(invalidData));
    }, [dispatch]);

    const getAllEntityIds = useCallback(() => Promise.resolve({
        type: '',
        payload: {
            data: templateData.map(({id}) => id),
            success: true as const,
            error: false as const,
        },
    }), [templateData]);

    const getHeaderTopOffset = useCallback(() => STICKY_HEADER_OFFSET, []);

    const noop = useCallback(() => undefined, []);

    const maxRowNumber = getPaginatedRowNumber({currentPage, pageSize, index: renderedRows?.length});
    const rows = useMemo(
        () => templateData.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(({id}) => id) || [],
        [templateData, currentPage, pageSize],
    );

    return (
        <BoxLoading loading={!templateData}>
            <ToolsRibbonComponent {...{
                selectionKey: SELECTION_KEY,
                rows,
                totalCount: templateData.length,
                showSelectAll: true,
                getAllEntityIds,
            }}>
                {ids => (
                    <Group>
                        <SetType {...{selectedRecordIds: ids as number[]}}/>
                        <SetStatus {...{selectedRecordIds: ids as number[], availableStatusCodes}}/>
                        <SetCountry {...{selectedRecordIds: ids as number[]}}/>
                        <SetGroup {...{accountId, selectedRecordIds: ids as number[]}}/>
                        <SetNewsletters {...{selectedRecordIds: ids as number[]}}/>
                        <RemoveRecord {...{selectedRecordIds: ids as number[]}}/>
                    </Group>
                )}
            </ToolsRibbonComponent>
            <WithInteractionTable
                dataTableId={DATA_TABLE_ID}
                columns={columns}
                dataRows={renderedRows}
                dataAPI={() => dataApi}
                trackingAPI={() => trackingApi}
                validator={validateColumnValue}
                onValidate={onValidate}>
                {(dataRows, interactiveColumns) => (
                    <DataTable
                        data={{rows: dataRows, columns: interactiveColumns}}
                        getHeaderTopOffset={getHeaderTopOffset}
                        onStopResize={onStopResize}
                        showNoDataMsg={!templateData.length}
                        tableMessage="The user list is empty."
                        headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                            switch (column.columnId) {
                                case COLUMNS.EMAIL:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}_${uniqueId()}`}
                                            column={column}
                                            resizable={true}>
                                            <MainColumnHeader
                                                selection={(
                                                    <SelectionHeader
                                                        getEntityIds={getAllEntityIds}
                                                        selectionKey={SELECTION_KEY}
                                                        entityIds={rows}
                                                        totalCount={templateData.length}
                                                    />
                                                )}
                                                maxRowNumber={maxRowNumber}>
                                                <strong>
                                                    {getValidationHeader(locale, templateData.length)}
                                                </strong>
                                            </MainColumnHeader>
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={`${column.columnId}`}
                                            column={column}
                                            resizable={true}>
                                            <TableHeader indent="none" className="search-header">
                                                <strong>{HEADERS[column.columnId]}</strong>
                                            </TableHeader>
                                        </Cell>
                                    );
                            }
                        }}
                        renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                            const columnValues = rowsParams.row.columnValues as Record<string, TemplateColumnValue>;
                            const {rowIndex, column: {columnId}} = rowsParams;
                            const {value} = columnValues[columnId];
                            const rowId = +rowsParams.row.id;
                            const column = interactiveColumns.find(
                                interactiveColumn => interactiveColumn.columnId === rowsParams.column.columnId,
                            ) || ({
                                ...rowsParams.column,
                                dataType: TDataType.STATIC,
                            });
                            const cellKey = uniqueId('cell');
                            const rowNumber = getPaginatedRowNumber({
                                currentPage,
                                pageSize,
                                index: rowIndex + 1,
                            });

                            let info = <div style={{width: 36}}/>;
                            if (columnValues.email.validationError) {
                                info = (
                                    <div style={{width: 20}}>
                                        <ErrorFlatButton
                                            icon={Icons.ERROR}
                                            label=""
                                            title={columnValues.email.validationError}
                                            onClick={noop}
                                        />
                                    </div>
                                );
                            } else if (columnValues.priorAccount.value) {
                                info = (
                                    <div style={{width: 20}}>
                                        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                            <BasicIconButton
                                                ref={anchorRef}
                                                {...getAnchorProps()}
                                                iconProps={{type: Icons.INFO}}
                                            />
                                        )}>
                                            {EXISTED_USER_MESSAGE}
                                        </Tooltip>
                                    </div>
                                );
                            }

                            switch (columnId) {
                                case COLUMNS.EMAIL:
                                    return (
                                        <MainColumnCell
                                            key={rowIndex + columnId + String(value)}
                                            {...{
                                                selection: (
                                                    <Group justify="end">
                                                        <SelectionRow
                                                            rows={rows}
                                                            index={rowIndex}
                                                            entityId={rowId}
                                                            selectionKey={SELECTION_KEY}
                                                            totalCount={templateData.length}
                                                        />
                                                        {info}
                                                    </Group>
                                                ),
                                                rowId,
                                                maxRowNumber,
                                                rowNumber,
                                                cellOptions: {...cellOptions, column},
                                            }}>
                                            {columnValues.priorAccount.value ? (
                                                <UserLink
                                                    userId={rowId}
                                                    caption={String(value)}
                                                    blank={true}
                                                    place={PLACE}
                                                />
                                            ) : (
                                                <EllipsisTextHint title={String(value)}>
                                                    {String(value)}
                                                </EllipsisTextHint>
                                            )}
                                        </MainColumnCell>
                                    );
                                case COLUMNS.PRIOR_ACCOUNT:
                                case COLUMNS.TARGET_ACCOUNT:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className={validateFileStepCell}>
                                            {value ? (
                                                <AccountLink
                                                    accountId={(value as AccountValue).accountId}
                                                    accountName={(value as AccountValue).accountName}
                                                    blank={true}
                                                    place={PLACE}
                                                />
                                            ) : '--'}
                                        </Cell>
                                    );
                                case COLUMNS.GROUP:
                                    const group = value as GroupValue;
                                    const groupCaption = group ? String(group.name || group.id) : '';
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className={validateFileStepCell}>
                                            {group ? (
                                                <EllipsisTextHint title={groupCaption}>
                                                    {groupCaption}
                                                </EllipsisTextHint>
                                            ) : '--'}
                                        </Cell>
                                    );
                                default:
                                    return (
                                        <Cell
                                            {...cellOptions}
                                            key={cellKey}
                                            column={column}
                                            className={validateFileStepCell}>
                                            <InteractiveCell {...{
                                                dataTableId: DATA_TABLE_ID,
                                                column,
                                                columnIndex: cellOptions.index + 1,
                                                rowIndex,
                                            }}>
                                                {value ? (
                                                    <EllipsisTextHint title={value.toString()}>
                                                        {value.toString()}
                                                    </EllipsisTextHint>
                                                ) : <span>--</span>}
                                            </InteractiveCell>
                                        </Cell>
                                    );
                            }
                        }}
                        paginationStripe={(
                            <PaginationStripe
                                pageSize={pageSize}
                                totalCount={templateData.length}
                                currentPage={currentPage}
                                onChangePaginationSize={onChangePageSize}
                                onPaginationChange={onPaginationChange}
                                goToPage={true}
                                sizeSwitcherPosition="LEFT"
                                sizeSwitcherType="ONLY_PAGE_SIZE"
                                sizes={DEFAULT_PAGE_SIZES}
                            />
                        )}
                    />
                )}
            </WithInteractionTable>
        </BoxLoading>
    );
};
